import { useEffect, useRef } from 'react';
import { type KeyboardShortcut, matchesShortcut } from '@/lib/platform';
import {
  currentShortcut,
  readShortcutOverrides,
  shortcutAllowedFrom,
} from '@/lib/settings-page/keyboard-shortcuts';

/** The shell's shortcuts (D1, D3b): one place, so menus show the same keys they answer. */
export const NEW_TASK_SHORTCUT: KeyboardShortcut = { key: 'n', commandOrControl: true };
export const TOGGLE_SIDEBAR_SHORTCUT: KeyboardShortcut = { key: 's', commandOrControl: true };
export const BACK_SHORTCUT: KeyboardShortcut = { key: '[', commandOrControl: true };
export const FORWARD_SHORTCUT: KeyboardShortcut = { key: ']', commandOrControl: true };
export const SETTINGS_SHORTCUT: KeyboardShortcut = { key: ',', commandOrControl: true };
export const SHORTCUTS_SHORTCUT: KeyboardShortcut = { key: '/', commandOrControl: true };
/** ⇧⌘E: the sidebar goes to the next side: the conversations, a docked core tool's list. */
export const SIDEBAR_SIDE_SHORTCUT: KeyboardShortcut = {
  key: 'e',
  commandOrControl: true,
  shift: true,
};

export type ShellShortcutHandlers = {
  newTask: () => void;
  toggleSidebar: () => void;
  navigate: (direction: -1 | 1) => void;
  openSettings: () => void;
  openShortcuts: () => void;
};

/** Which handler a key press runs, if any. */
export function shellShortcutFor(
  event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'code'>,
): Exclude<keyof ShellShortcutHandlers, 'navigate'> | 'back' | 'forward' | null {
  // The keys Settings › Keyboard shortcuts gives them, else these.
  const overrides = readShortcutOverrides();
  const keys = (id: string, fallback: KeyboardShortcut) =>
    currentShortcut(id, overrides) ?? fallback;
  if (matchesShortcut(event, keys('new-task', NEW_TASK_SHORTCUT))) return 'newTask';
  if (matchesShortcut(event, keys('toggle-sidebar', TOGGLE_SIDEBAR_SHORTCUT)))
    return 'toggleSidebar';
  if (matchesShortcut(event, keys('back', BACK_SHORTCUT))) return 'back';
  if (matchesShortcut(event, keys('forward', FORWARD_SHORTCUT))) return 'forward';
  if (matchesShortcut(event, keys('settings', SETTINGS_SHORTCUT))) return 'openSettings';
  if (matchesShortcut(event, keys('keyboard-shortcuts', SHORTCUTS_SHORTCUT)))
    return 'openShortcuts';
  return null;
}

/** Answers the shell's shortcuts from anywhere in the window. */
export function useShellShortcuts(handlers: ShellShortcutHandlers, enabled = true): void {
  const current = useRef(handlers);
  current.current = handlers;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (event: KeyboardEvent) => {
      // Typing in a field keeps its keys: there, only ⌘/Ctrl shortcuts run.
      if (event.defaultPrevented || !shortcutAllowedFrom(event)) return;
      const shortcut = shellShortcutFor(event);
      if (shortcut === null) return;
      event.preventDefault();
      const run = current.current;
      if (shortcut === 'back') run.navigate(-1);
      else if (shortcut === 'forward') run.navigate(1);
      else run[shortcut]();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
}
